import { type KycReadCitizenCardFrontMutation } from '@e-kyc/graphql';
import { i18n } from '@e-kyc/translations';
import { createContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { LoadingSpinner } from '../../components';
import CompletedPage from '../../pages/_completed';
import ErrorPage from '../../pages/_error';

import { DEFAULT_LANGUAGE } from './constants';
import { useEKycUser, useEkycStatus, useEKycNavigation } from './hooks';
import { type EkycContextValue } from './types';

export const EkycContext = createContext<EkycContextValue | undefined>(undefined);

export interface EKycProviderProps {
  children: React.ReactNode;
}

export function EKycProvider({ children }: EKycProviderProps) {
  const [searchParams] = useSearchParams();
  const language = searchParams.get('lang') || DEFAULT_LANGUAGE;

  const { userId, loading: userLoading, error: userError } = useEKycUser();
  const {
    isCompleted,
    loading: eKycLoading,
    error: ekycError,
    refetch: refetchEkycStatus,
  } = useEkycStatus(userId);
  const { step, setStep, goBackToPreviousStep } = useEKycNavigation();

  // Citizen card data state
  const [citizenCardData, setCitizenCardData] =
    useState<KycReadCitizenCardFrontMutation['kycReadCitizenCardFront']>();

  // Set language
  useEffect(() => {
    i18n.changeLanguage(language);
  }, [language]);

  // Loading states
  if (userLoading || eKycLoading) {
    return (
      <div className='flex h-screen items-center justify-center'>
        <LoadingSpinner />
      </div>
    );
  }

  // Handle case no token - so api did not request
  if (!userError && !ekycError && !userId) {
    return (
      <ErrorPage title='token.error.missing.title' description='token.error.missing.description' />
    );
  }

  // Handle error/expired states - clean up token
  if (userError || ekycError || !userId) {
    if (userError?.message.includes('Token expired')) {
      return (
        <ErrorPage
          title='token.error.expired.title'
          description='token.error.expired.description'
        />
      );
    }
    if (userError?.message.includes('Token not found')) {
      return (
        <ErrorPage
          title='token.error.invalid.title'
          description='token.error.invalid.description'
        />
      );
    }
    return (
      <ErrorPage
        title='token.error.connection.title'
        description='token.error.connection.description'
      />
    );
  }

  // Show completed page if e-KYC is done - clean up token
  if (isCompleted) {
    return <CompletedPage />;
  }

  return (
    <EkycContext.Provider
      value={{
        step,
        setStep,
        goBackToPreviousStep,
        setCitizenCardData,
        citizenCardData,
        userId,
        refetchEkycStatus,
      }}
    >
      {children}
    </EkycContext.Provider>
  );
}
